import { useState } from 'react';
import { AxiosResponse } from 'axios';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import withSidebar from '../../hoc/withSideBar/withSideBar';
import { defaultToastData } from '../../helpers/commonHelper';
import { _TmaBranding, _TmaLogoType, deleteTmaBrandingLogo, getTmaBranding, saveTmaBranding } from '../../helpers/tmaBrandingHelper';
import { getErrorMessage } from '../../utils/common';
import logger from '../../utils/logger';
import { AlertVariant } from '../../constants/constants';
import { TMA_BRANDING } from '../../constants/queryKeys';
import { _ToastMessageProps } from '../../constants/staticTypes';
import {
    BRANDING_UPDATED,
    CANCEL,
    DROP_MOBILE_LOGO,
    DROP_WEB_LOGO,
    INVALID_LOGO_FILE_TYPE,
    LOGO,
    LOGO_DELETED,
    LOGO_SECTION_DESC,
    MOBILE_LOGO_RECOMMENDATION,
    SETTINGS,
    SUPER_ADMIN_PORTAL,
    SUPER_ADMIN_SETTINGS_INFO_PREFIX,
    SUPER_ADMIN_SETTINGS_INFO_SUFFIX,
    UPDATE,
    UPLOAD_MOBILE_LOGO,
    UPLOAD_WEB_LOGO,
    WEB_LOGO_RECOMMENDATION,
} from '../../constants/strings';
import { useTranslation } from '../../hooks/useTranslation';
import MlxBlockUI from '../../components/common/MlxBlockUI/MlxBlockUI';
import MlxButton from '../../components/common/MlxButton/MlxButton';
import MlxConfirmPopup from '../../components/common/MlxConfirmPopup/MlxConfirmPopup';
import MlxToast from '../../components/common/MlxToast/MlxToast';
import LogoDropzone from './components/LogoDropzone';
import './Settings.scss';

const languageTexts = {
    SETTINGS: SETTINGS,
    SUPER_ADMIN_SETTINGS_INFO_PREFIX: SUPER_ADMIN_SETTINGS_INFO_PREFIX,
    SUPER_ADMIN_PORTAL: SUPER_ADMIN_PORTAL,
    SUPER_ADMIN_SETTINGS_INFO_SUFFIX: SUPER_ADMIN_SETTINGS_INFO_SUFFIX,
    LOGO: LOGO,
    LOGO_SECTION_DESC: LOGO_SECTION_DESC,
    UPLOAD_WEB_LOGO: UPLOAD_WEB_LOGO,
    UPLOAD_MOBILE_LOGO: UPLOAD_MOBILE_LOGO,
    DROP_WEB_LOGO: DROP_WEB_LOGO,
    DROP_MOBILE_LOGO: DROP_MOBILE_LOGO,
    WEB_LOGO_RECOMMENDATION: WEB_LOGO_RECOMMENDATION,
    MOBILE_LOGO_RECOMMENDATION: MOBILE_LOGO_RECOMMENDATION,
    INVALID_LOGO_FILE_TYPE: INVALID_LOGO_FILE_TYPE,
    CANCEL: CANCEL,
    UPDATE: UPDATE,
    BRANDING_UPDATED: BRANDING_UPDATED,
    LOGO_DELETED: LOGO_DELETED,
};

function Settings() {
    const queryClient = useQueryClient();
    const translatedText = useTranslation(languageTexts, 'none');

    const [toastMessage, setToastMessage] = useState<_ToastMessageProps>(defaultToastData);
    const [webLogoFile, setWebLogoFile] = useState<File | null>(null);
    const [mobileLogoFile, setMobileLogoFile] = useState<File | null>(null);
    // bumping the key remounts the dropzones, clearing their previews
    const [resetKey, setResetKey] = useState<number>(0);

    const showToast = (message: string, variant: AlertVariant) => {
        setToastMessage({ message, variant, show: true });
    };

    const brandingQuery = useQuery({
        queryKey: TMA_BRANDING,
        queryFn: () => getTmaBranding(queryClient),
        onError: (error) => {
            logger.error('Failed to fetch TMA branding', error);
            showToast(getErrorMessage(error), AlertVariant.ERROR);
        },
        refetchOnWindowFocus: false,
    });
    const saveBrandingMutation = useMutation(saveTmaBranding);
    const deleteLogoMutation = useMutation(deleteTmaBrandingLogo);

    const branding: _TmaBranding | undefined = brandingQuery.data?.data?.data;
    const savedWebLogoUrl = branding?.webLogo || null;
    const savedMobileLogoUrl = branding?.mobileLogo || null;

    const hasChanges = webLogoFile !== null || mobileLogoFile !== null;
    const isBusy = saveBrandingMutation.isLoading || deleteLogoMutation.isLoading;

    const handleInvalidFile = () => {
        showToast(translatedText.INVALID_LOGO_FILE_TYPE ?? INVALID_LOGO_FILE_TYPE, AlertVariant.ERROR);
    };

    const handleCancel = () => {
        setWebLogoFile(null);
        setMobileLogoFile(null);
        setResetKey((prev) => prev + 1);
    };

    const handleUpdate = () => {
        saveBrandingMutation.mutate(
            { webLogoFile, mobileLogoFile },
            {
                onSuccess: (response: AxiosResponse) => {
                    showToast(response.data?.message ?? translatedText.BRANDING_UPDATED ?? BRANDING_UPDATED, AlertVariant.SUCCESS);
                    handleCancel();
                    brandingQuery.refetch();
                },
                onError: (error) => showToast(getErrorMessage(error), AlertVariant.ERROR),
            }
        );
    };

    const handleDeleteLogo = (logoType: _TmaLogoType) => {
        deleteLogoMutation.mutate(logoType, {
            onSuccess: (response: AxiosResponse) => {
                showToast(response.data?.message ?? translatedText.LOGO_DELETED ?? LOGO_DELETED, AlertVariant.SUCCESS);
                brandingQuery.refetch();
            },
            onError: (error) => showToast(getErrorMessage(error), AlertVariant.ERROR),
        });
    };

    return (
        <MlxBlockUI isLoading={brandingQuery.isLoading || brandingQuery.isFetching || deleteLogoMutation.isLoading} opacity={0.5}>
            <div className="page-section h-100 admin-settings">
                <MlxToast
                    data-testid="toast"
                    message={toastMessage.message}
                    onClose={() => setToastMessage(defaultToastData)}
                    show={toastMessage.show}
                    variant={toastMessage.variant}
                />
                <MlxConfirmPopup />
                <span className="page-header">{translatedText.SETTINGS}</span>
                <div className="admin-settings-info">
                    <i className="ri-information-line me-2"></i>
                    <span>
                        {translatedText.SUPER_ADMIN_SETTINGS_INFO_PREFIX} <strong>{translatedText.SUPER_ADMIN_PORTAL}</strong>{' '}
                        {translatedText.SUPER_ADMIN_SETTINGS_INFO_SUFFIX}
                    </span>
                </div>
                <div className="admin-settings-card">
                    <div className="admin-settings-card-header">
                        <span className="admin-settings-card-title">{translatedText.LOGO}</span>
                        <span className="admin-settings-card-desc">{translatedText.LOGO_SECTION_DESC}</span>
                    </div>
                    <div className="admin-settings-card-body">
                        <LogoDropzone
                            key={`web-logo-${resetKey}`}
                            id="web-logo"
                            label={translatedText.UPLOAD_WEB_LOGO}
                            dropText={translatedText.DROP_WEB_LOGO}
                            hint={translatedText.WEB_LOGO_RECOMMENDATION}
                            savedUrl={savedWebLogoUrl}
                            disabled={isBusy}
                            onFileChange={setWebLogoFile}
                            onInvalidFile={handleInvalidFile}
                            onRemoveSaved={() => handleDeleteLogo('webLogo')}
                        />
                        <LogoDropzone
                            key={`mobile-logo-${resetKey}`}
                            id="mobile-logo"
                            label={translatedText.UPLOAD_MOBILE_LOGO}
                            dropText={translatedText.DROP_MOBILE_LOGO}
                            hint={translatedText.MOBILE_LOGO_RECOMMENDATION}
                            savedUrl={savedMobileLogoUrl}
                            disabled={isBusy}
                            onFileChange={setMobileLogoFile}
                            onInvalidFile={handleInvalidFile}
                            onRemoveSaved={() => handleDeleteLogo('mobileLogo')}
                        />
                    </div>
                    <div className="settings-footer">
                        <MlxButton
                            label={translatedText.CANCEL}
                            onClick={(event) => {
                                event.preventDefault();
                                handleCancel();
                            }}
                            icon="ri-close-large-line"
                            iconPos="right"
                            size="small"
                            outlined
                            disabled={isBusy}
                        />
                        <MlxButton
                            className="ms-2"
                            label={translatedText.UPDATE}
                            onClick={(event) => {
                                event.preventDefault();
                                handleUpdate();
                            }}
                            icon="ri-check-line"
                            iconPos="right"
                            size="small"
                            disabled={!hasChanges || isBusy || brandingQuery.isLoading}
                            loading={saveBrandingMutation.isLoading}
                        />
                    </div>
                </div>
            </div>
        </MlxBlockUI>
    );
}

export default withSidebar(Settings);
